<form class="flex flex-col gap-4" [formGroup]="form">
  <pr-content-container>
    <section class="flex flex-col gap-4">
      <pr-deposit-section-heading
        anchor="deposits"
        heading="Do you take deposits?"
      />
      <div class="flex flex-row justify-between gap-4">
        <div class="max-w-prose">
          <p>
            Set up how deposits work: when they're taken, how much, and what
            happens when an appointment is cancelled. These settings apply to
            every practice in the brand.
          </p>
          @if (!form.controls.enabled.value) {
            <p>
              Deposits are off. Turn them on to choose which bookings need a
              deposit, set amounts per treatment, and decide what patients
              forfeit when they cancel.
            </p>
          }
        </div>
        <mat-slide-toggle
          class="py-2"
          formControlName="enabled"
          color="primary"
          aria-label="Enable deposits"
          ptElementTarget="enable-deposits-toggle"
        />
      </div>
    </section>
  </pr-content-container>

  @if (form.controls.enabled.value) {
    <pr-content-container>
      <section class="flex flex-col gap-4">
        <pr-deposit-section-heading
          anchor="timing"
          heading="When is a deposit taken?"
        />

        <div class="flex flex-col gap-2">
          <p class="!mb-0 text-sm text-slate-600">
            Every booking is checked in this order. The first match wins.
          </p>
          <ol class="!mb-0 list-decimal space-y-1 pl-5 text-sm text-slate-600">
            <li>
              <span class="font-medium">Treatment Configuration</span> — the
              treatment being booked has its own deposit amount.
            </li>
            <li>
              <span class="font-medium">Patient</span> — the patient is flagged
              as deposit required. Applies to any booking type.
            </li>
            <li>
              <span class="font-medium">Treatment Template</span> — the
              appointment type being booked needs one, narrowed to certain
              booking types or days if you want.
            </li>
          </ol>
          <p class="!mb-0 text-xs opacity-50">
            Nothing matches, no deposit. If something matches but no amount is
            set anywhere, staff see a warning and nothing is taken.
          </p>
        </div>

        <div class="w-full border-b border-solid border-slate-200"></div>

        <div class="flex flex-col divide-y divide-slate-200">
          <div
            class="flex flex-col items-start gap-4 py-4 first:pt-0 last:pb-0 sm:flex-row"
          >
            <div class="flex flex-col gap-1 sm:flex-1">
              <span class="font-medium">Which bookings need a deposit?</span>
              <p class="!mb-0 max-w-prose text-xs text-slate-600">
                A Treatment Template that requires a deposit only asks for one
                on these booking types. Individual templates can narrow this
                further.
              </p>
              <p class="!mb-0 max-w-prose text-xs opacity-50">
                A deposit your team takes at the front desk isn't covered.
              </p>
            </div>

            <div class="w-full shrink-0 pt-2 sm:w-80">
              <mat-form-field class="w-full" subscriptSizing="dynamic">
                <mat-label>Booking types</mat-label>
                <mat-select
                  formControlName="appliesTo"
                  multiple
                  aria-label="Default deposit booking types"
                >
                  @for (context of bookingContexts; track context) {
                    <mat-option [value]="context">
                      {{ bookingContextLabels[context] }}
                    </mat-option>
                  }
                </mat-select>
                @if (form.controls.appliesTo.hasError('required')) {
                  <mat-error>Select at least one booking type</mat-error>
                }
              </mat-form-field>
            </div>
          </div>

          <div
            class="flex flex-col items-start gap-4 py-4 first:pt-0 last:pb-0 sm:flex-row"
          >
            <div class="flex flex-col gap-1 sm:flex-1">
              <span class="font-medium">When is the money taken?</span>
              <p class="!mb-0 max-w-prose text-xs text-slate-600">
                Each option says what happens for online bookings and for
                bookings your team makes. {{ depositTimingDefaultHint }}
              </p>
              <p class="!mb-0 max-w-prose text-xs opacity-50">
                Applies to card payments through Stripe only.
              </p>
            </div>

            <div class="w-full shrink-0 pt-2 sm:w-80">
              <mat-form-field class="w-full" subscriptSizing="dynamic">
                <mat-label>{{ depositTimingDefaultQuestion }}</mat-label>
                <mat-select formControlName="captureStrategy">
                  <mat-select-trigger>
                    @if (form.controls.captureStrategy.value; as strategy) {
                      {{ captureStrategyText[strategy].label }}
                    }
                  </mat-select-trigger>
                  @for (option of captureStrategyOptions; track option.value) {
                    <mat-option [value]="option.value">
                      <div class="flex flex-col py-1">
                        <span class="font-medium">{{ option.text.label }}</span>
                        <span class="text-xs">{{
                          option.text.description
                        }}</span>
                      </div>
                    </mat-option>
                  }
                </mat-select>
              </mat-form-field>
            </div>
          </div>

          <div
            class="flex flex-col items-start gap-4 py-4 first:pt-0 last:pb-0 sm:flex-row"
          >
            <div class="flex flex-col gap-1 sm:flex-1">
              <span class="font-medium"
                >Patients flagged as deposit required</span
              >
              <p class="!mb-0 max-w-prose text-xs text-slate-600">
                Applies to staff bookings only. If the Treatment Template being
                booked already sets a deposit, that amount is used instead.
                Leave this blank and flagged patients are not charged a deposit.
              </p>
              <p class="!mb-0 max-w-prose text-xs opacity-50">
                Collected by a payment link we send the patient.
              </p>
            </div>

            <div class="w-full shrink-0 pt-2 sm:w-80">
              <mat-form-field class="w-full" subscriptSizing="dynamic">
                <mat-label
                  >Deposit for patients flagged as deposit required</mat-label
                >
                <span matTextPrefix>$&nbsp;</span>
                <input
                  matInput
                  type="number"
                  formControlName="patientDepositAmountDollars"
                  min="0.01"
                  placeholder="None"
                />
                @if (hasPatientDepositAmount) {
                  <button
                    matSuffix
                    mat-icon-button
                    type="button"
                    aria-label="Clear the deposit for flagged patients"
                    (click)="clearPatientDepositAmount()"
                  >
                    <mat-icon>close</mat-icon>
                  </button>
                }
                @if (
                  form.controls.patientDepositAmountDollars.hasError('min')
                ) {
                  <mat-error>Deposit amount must be greater than 0</mat-error>
                }
              </mat-form-field>
            </div>
          </div>

          <div
            class="flex flex-col items-start gap-4 py-4 first:pt-0 last:pb-0 sm:flex-row"
          >
            <div class="flex flex-col gap-1 sm:flex-1">
              <span class="font-medium"
                >Apply account credit from deposits at checkout</span
              >
              <p class="!mb-0 max-w-prose text-xs text-slate-600">
                When enabled, account credit from an appointment's deposit is
                applied to its invoice at checkout.
              </p>
              <p class="!mb-0 max-w-prose text-xs opacity-50">
                Never more than the invoice owes. Anything left over stays as
                account credit.
              </p>
            </div>

            <div class="w-full shrink-0 pt-2 sm:w-80">
              <mat-slide-toggle
                formControlName="autoApplyAppointmentAccountCredits"
                color="primary"
                aria-label="Automatically apply account credit from deposits at appointment checkout"
              />
            </div>
          </div>
        </div>
      </section>
    </pr-content-container>

    <pr-content-container>
      <section class="flex flex-col gap-4">
        <pr-deposit-section-heading
          anchor="treatment-templates"
          heading="Treatment Templates"
        />
        <p class="!mb-0 text-sm text-slate-600">
          Require a deposit for a whole appointment type. This is what someone
          chooses when they book, online or with your team.
        </p>
        @for (group of treatmentTemplateGroups; track group.label) {
          <div
            class="flex flex-col gap-2"
            role="group"
            [attr.aria-labelledby]="'treatment-template-group-' + $index"
          >
            <p
              class="!mb-0 font-bold"
              [id]="'treatment-template-group-' + $index"
            >
              {{ group.label }}
            </p>
            @if (group.bookableOnline) {
              <p class="!mb-0 text-sm text-slate-600">
                Treatments bookable online take a fixed dollar deposit, because
                the final price isn't known at the time of booking.
              </p>
            }
            <div class="flex flex-col gap-3">
              @for (row of group.rows; track row.template.ref.path) {
                <pr-deposit-template-row [row]="row" [currency]="currency" />
              } @empty {
                <p class="py-2 text-xs opacity-50">
                  No Treatment Templates are configured in this group.
                </p>
              }
            </div>
          </div>
        }
      </section>
    </pr-content-container>

    <pr-content-container>
      <pr-deposit-treatment-configuration-settings
        [treatmentConfigurations]="treatmentConfigurations"
        [value]="treatmentConfigurationDeposits"
        (stateChange)="treatmentConfigurationStateChanged($event)"
      />
    </pr-content-container>

    <pr-content-container>
      <section class="flex flex-col gap-4">
        <pr-deposit-section-heading
          anchor="forfeiture"
          heading="When does the patient forfeit their deposit?"
        />

        <div class="flex flex-col divide-y divide-slate-200">
          <div
            class="flex flex-col items-start gap-4 py-4 first:pt-0 last:pb-0 sm:flex-row"
          >
            <div class="flex flex-col gap-1 sm:flex-1">
              <span class="font-medium">Who approves a forfeit?</span>
              <p class="!mb-0 max-w-prose text-xs text-slate-600">
                Decide whether Principle acts on your rules by itself, or leaves
                the call to whoever is cancelling the appointment.
              </p>
            </div>

            <div class="w-full shrink-0 pt-2 sm:w-80">
              <mat-form-field class="w-full" subscriptSizing="dynamic">
                <mat-label>Forfeiture Policy</mat-label>
                <mat-select formControlName="forfeiturePolicy">
                  <mat-select-trigger>
                    @if (form.controls.forfeiturePolicy.value; as policy) {
                      {{ forfeiturePolicyText[policy].label }}
                    }
                  </mat-select-trigger>
                  @for (option of forfeiturePolicyOptions; track option.value) {
                    <mat-option [value]="option.value">
                      <div class="flex flex-col py-1">
                        <span class="font-medium">{{ option.text.label }}</span>
                        <span class="text-xs">{{
                          option.text.description
                        }}</span>
                      </div>
                    </mat-option>
                  }
                </mat-select>
              </mat-form-field>
            </div>
          </div>

          <div
            class="flex flex-col items-start gap-4 py-4 first:pt-0 last:pb-0 sm:flex-row"
          >
            <div class="flex flex-col gap-1 sm:flex-1">
              <span class="font-medium">
                Who receives the cancellation fee?
              </span>
              <p class="!mb-0 max-w-prose text-xs text-slate-600">
                The default for every forfeited deposit. Staff who can override
                deposits can change it when they cancel or reschedule.
              </p>
            </div>

            <div class="w-full shrink-0 pt-2 sm:w-80">
              <mat-form-field class="w-full" subscriptSizing="dynamic">
                <mat-label>Cancellation Fee Goes To</mat-label>
                <mat-select
                  formControlName="cancellationFeeAttribution"
                  ptElementTarget="deposit-settings-cancellation-fee-attribution"
                >
                  <mat-select-trigger>
                    @if (
                      form.controls.cancellationFeeAttribution.value;
                      as attribution
                    ) {
                      {{ cancellationFeeAttributionText[attribution].label }}
                    }
                  </mat-select-trigger>
                  @for (
                    option of cancellationFeeAttributionOptions;
                    track option.value
                  ) {
                    <mat-option [value]="option.value">
                      <div class="flex flex-col py-1">
                        <span class="font-medium">{{ option.text.label }}</span>
                        <span class="text-xs">{{
                          option.text.description
                        }}</span>
                      </div>
                    </mat-option>
                  }
                </mat-select>
              </mat-form-field>
            </div>
          </div>

          <div class="flex flex-col gap-2 py-4 first:pt-0 last:pb-0">
            <span class="font-medium">
              Which reasons forfeit a deposit, and how much?
            </span>
            <p class="!mb-0 max-w-prose text-xs text-slate-600">
              Tick a reason to let it forfeit a deposit, then set how much of
              the deposit the practice keeps. 100% keeps the whole deposit, 50%
              keeps half. Untick a reason and it never forfeits
            </p>

            <div class="mt-2 flex flex-col divide-y divide-slate-200">
              @for (row of reasonRows; track row.reason.ref.path) {
                <div class="flex min-h-[56px] items-center gap-4 py-2">
                  <mat-checkbox
                    class="min-w-0 flex-1"
                    color="primary"
                    [checked]="row.rule !== undefined"
                    [disabled]="form.controls.forfeitureRules.disabled"
                    (change)="
                      forfeitureReasonSelectionChanged(
                        row.reason.ref,
                        $event.checked
                      )
                    "
                  >
                    <span class="flex flex-col">
                      <span class="font-medium">{{ row.reason.name }}</span>
                      <span class="text-xs opacity-50">
                        @if (row.reason.hrsBeforeAppointment !== undefined) {
                          Forfeit Deposit within
                          {{ row.reason.hrsBeforeAppointment }} hours of the
                          appointment
                        } @else {
                          Forfeit Deposit any time before the appointment
                        }
                      </span>
                    </span>
                  </mat-checkbox>
                  @if (row.rule) {
                    <mat-form-field class="w-32" subscriptSizing="dynamic">
                      <mat-label>Forfeit</mat-label>
                      <input
                        matInput
                        type="number"
                        required
                        min="1"
                        max="100"
                        step="1"
                        [formControl]="row.rule.controls.forfeiturePercentage"
                        [attr.aria-label]="
                          row.reason.name + ' forfeiture percentage'
                        "
                      />
                      <span matTextSuffix>&nbsp;%</span>
                      @if (
                        row.rule.controls.forfeiturePercentage.hasError(
                          'required'
                        )
                      ) {
                        <mat-error>Forfeiture percentage is required</mat-error>
                      }
                      @if (
                        row.rule.controls.forfeiturePercentage.hasError('min')
                      ) {
                        <mat-error>Must be at least 1%</mat-error>
                      }
                      @if (
                        row.rule.controls.forfeiturePercentage.hasError('max')
                      ) {
                        <mat-error>Cannot exceed 100%</mat-error>
                      }
                      @if (
                        row.rule.controls.forfeiturePercentage.hasError(
                          'pattern'
                        )
                      ) {
                        <mat-error>Must be a whole percentage</mat-error>
                      }
                    </mat-form-field>
                  }
                </div>
              } @empty {
                <p class="py-2 text-xs opacity-50">
                  No rescheduling or cancellation reasons are configured. Add
                  them under Rescheduling &amp; Cancellation Reasons before
                  setting up forfeits.
                </p>
              }
            </div>
          </div>

          <div class="w-full border-b border-solid border-slate-200"></div>

          <div
            class="flex flex-col items-start gap-4 py-4 first:pt-0 last:pb-0 sm:flex-row"
          >
            <div class="flex flex-col gap-1 sm:flex-1">
              <span class="font-medium"
                >Where is your cancellation policy?</span
              >
              <p class="!mb-0 max-w-prose text-xs text-slate-600">
                A link to your written cancellation policy. It is shown to
                patients before they pay a deposit, and in the Deposit Forfeit
                Notice Email.
              </p>
              @if (form.allowsMissingCancellationPolicyUrl) {
                <p class="!mb-0 max-w-prose text-xs opacity-50">
                  This brand was set up before a link was required, so you can
                  save without one for now. Turning the forfeit notification
                  email off and back on will make it required.
                </p>
              }
            </div>

            <div class="w-full shrink-0 pt-2 sm:w-80">
              <mat-form-field class="w-full" subscriptSizing="dynamic">
                <mat-label>Cancellation policy link</mat-label>
                <input
                  matInput
                  type="url"
                  formControlName="cancellationPolicyUrl"
                  placeholder="https://example.com/cancellation-policy"
                />
                @if (form.controls.cancellationPolicyUrl.hasError('required')) {
                  <mat-error>
                    A cancellation policy link is required when the forfeit
                    notification email is enabled
                  </mat-error>
                }
              </mat-form-field>
            </div>
          </div>
        </div>
      </section>
    </pr-content-container>
    <pr-content-container>
      <section class="flex flex-col gap-4">
        <pr-deposit-section-heading
          anchor="messages"
          heading="Which messages do we send about deposits?"
        />

        <div class="flex flex-col divide-y divide-slate-200">
          @for (
            entry of depositCommunicationEntries;
            track entry.systemTemplate
          ) {
            <div
              class="flex flex-col items-start gap-4 py-4 first:pt-0 last:pb-0 sm:flex-row"
            >
              <div class="flex flex-col gap-1 sm:flex-1">
                <span class="font-medium">{{ entry.displayName }}</span>

                <p class="!mb-0 max-w-prose text-xs text-slate-600">
                  {{ entry.purpose }}
                </p>

                @if (entry.controlType === 'always-sent') {
                  <p class="!mb-0 max-w-prose text-xs opacity-50">
                    This message can't be turned off.
                  </p>
                } @else if (entry.controlType === 'automation') {
                  <p class="!mb-0 max-w-prose text-xs opacity-50">
                    Timing and on/off are configured in
                    @if (brandUid | map: automationsLink; as automationsRoute) {
                      <a
                        class="text-primary-500 underline"
                        [routerLink]="automationsRoute"
                        >Brand Settings → Automations</a
                      >.
                    } @else {
                      Brand Settings → Automations.
                    }
                  </p>
                }
              </div>

              <div class="flex shrink-0 items-center gap-8 pt-2 sm:items-start">
                @if (entry.controlType === 'toggle') {
                  @if (entry.formControlName; as controlName) {
                    <mat-slide-toggle
                      labelPosition="before"
                      color="primary"
                      [formControlName]="controlName"
                      [aria-label]="entry.accessibleName"
                    >
                      Enable Message &nbsp;
                    </mat-slide-toggle>
                  }
                } @else if (
                  entry.controlType === 'automation' && !hasReminderAutomation
                ) {
                  <button
                    class="sm:-mt-2"
                    type="button"
                    mat-stroked-button
                    color="primary"
                    [disabled]="busy"
                    (click)="createReminderAutomation.emit()"
                  >
                    Set up reminder automation
                  </button>
                }

                @if (
                  entry.controlType !== 'automation' || hasReminderAutomation
                ) {
                  @if (
                    entry.systemTemplate | pairMap: brandUid : templateLink;
                    as link
                  ) {
                    <a
                      class="text-primary-500"
                      [routerLink]="link"
                      [attr.aria-label]="
                        'View ' + entry.displayName + ' template'
                      "
                      >View Template</a
                    >
                  }
                }
              </div>
            </div>
          }
        </div>
      </section>
    </pr-content-container>
  }
</form>
